<template>
	<div class="fd-filter-editor" :class="{ 'require-big-value':multivalue }">
		<template v-if="multivalue">
			<textarea v-if="bigValue" @change="textAreaChanged" v-model="arrayAsString" class="width100p value-editor"></textarea>
			<template v-else>
				<component v-if="suggester" :is="componentOfPart(multiSuggesterPart)" :data="fi" :part="multiSuggesterPart" :readonly="false" :viewmode="false"></component>
				<array-part v-else :data="fi" :part="arrayPart"></array-part>
			</template>
		</template>
		<component v-else :is="componentOfPart(singleItemPart)" :data="singleValue" :part="singleItemPart" :readonly="false" :viewmode="false"></component>
	</div>
</template>

<script setup lang="ts">
import type {ColumnListItem} from "@t/agenda/Columns";
import {computed, ref, watch} from "vue";
import ArrayPart from "@fd/containers/ArrayPart.vue";
import type {Array, ArrayAddButton} from "@t/fd/layout/Array";
import {FilterEntry, type FilterGroup} from "@t/agenda/Filter";
import {FormPart} from "@t/fd/FormPart";
import {type ColType, ColumnType} from "@t/agenda/Columns";
import type {Temporal} from "@t/fd/editor/Temporal";
import {Input} from "@t/fd/editor/Input";
import {injectComponentOfPart} from "@fd/PartInjectKeys";
import {Select} from "@t/fd/editor/Select";
import {Suggester, TagRule} from "@t/fd/editor/Suggester";
import {injectEnv} from "@fd/Environment";
import {arrayToText, textToArray} from "@fd/editors/filter/FilterUtils.ts";
import {SrcType} from "@t/meta/SourceType.ts";

const props = defineProps<{
	fi: FilterEntry,
	column: ColumnListItem,
	bigValue: boolean
}>();
const emit = defineEmits(['bigvalue']);

const op = computed(() => props.fi.op);
const componentOfPart = injectComponentOfPart();

class ValueProxy {
	title;	// this is for suggester editing to store item label

	constructor(title) {
		this.title = title;
	}

	get data():string {
		if (Array.isArray(props.fi.values)) {
			if (props.fi.values.length > 0) return props.fi.values[0];
		}
		return "";
	}

	set data(v:string) {
		if (Array.isArray(props.fi.values)) {
			if (props.fi.values.length > 0) props.fi.values[0] = v;
			else props.fi.values.push(v);
		} else {
			props.fi.values = [ v ];
		}
	}
}

const singleValue = new ValueProxy("");

const arrayAsString = ref<string>(arrayToText(props.fi.values));

function textAreaChanged() {
	props.fi.values = textToArray(arrayAsString.value);
}

function arrayChanged() {
	arrayAsString.value = arrayToText(props.fi.values);
}

const multivalue = computed<boolean>(() => {
	return op.value === 14 || op.value === 15; /* IN || NOT IN*/
});
const textAreaValue = computed<boolean>(() => multivalue.value && props.bigValue);
const useTextArea = ref<boolean>(textAreaValue.value);


const SUGGESTER_COLUMN_TYPES = [ ColumnType.PARTY, ColumnType.OPPORTUNITY, ColumnType.OFFER, ColumnType.BUSCASE,
	ColumnType.TASK, ColumnType.EVENT, ColumnType.PROBLEM, ColumnType.REGION,
	ColumnType.CAMPAIGN, ColumnType.PRODUCT ];

const suggester = computed<boolean>(()=>{
	const t = props.column.type;
	for (let st of SUGGESTER_COLUMN_TYPES) {
		if (st === t) return true;
	}
	return false;
});

/**
 * @param arrayItem true = this part will be used inside ArrayPart as item part
 * 					false = this item will be used on ValueProxy
 */
function itemPart(arrayItem:boolean):FormPart {
	let fp:FormPart;
	switch (props.column.type) {
		case ColumnType.DATE:
			fp = <Temporal>{
				type : "temporal",
				symbolic: true,
				timePolicy: 0 /* DATE */
			};
			break;
		case ColumnType.DATETIME:
			fp = <Temporal>{
				type : "temporal",
				symbolic: true,
				timePolicy: 2 /* DATETIME */
			};
			break;
		case ColumnType.DATE_AND_TIME:
			fp = <Temporal>{
				type : "temporal",
				symbolic: true,
				timePolicy: 1 /* UNIVERSAL */
			};
			break;
		case ColumnType.SET:
		case ColumnType.MULTIPLE:
			fp = <Select>{
				type: "select",
				list: props.column.list,
				//emptyItem: { value: "", name: ""},
				rewriteUnlisted: ""
			};
			break;
		case ColumnType.FLOAT:
			fp = <Input>{
				type: "input",
			}
			break;
		case ColumnType.INT:
		case ColumnType.LONG:
			if (props.column.list) {
				fp = <Select>{
					type: "select",
					list: props.column.list,
					//emptyItem: { value: "", name: ""},
					rewriteUnlisted: ""
				};
			} else {
				fp = <Input>{
					type: "input",
					itype: "number",
					cast: "string"
				};
			}
			break;
		case ColumnType.USER:
		case ColumnType.FULLTEXT:
			fp = <Select>{
				type: "select",
				list: props.column.list || "ALL_USER_IDS",
				//emptyItem: { value: "", name: ""},
				rewriteUnlisted: ""
			};
			break;
		case ColumnType.PARTY:
		case ColumnType.OPPORTUNITY:
		case ColumnType.OFFER:
		case ColumnType.BUSCASE:
		case ColumnType.TASK:
		case ColumnType.EVENT:
		case ColumnType.PROBLEM:
		case ColumnType.REGION:
		case ColumnType.CAMPAIGN:
		case ColumnType.PRODUCT:
			const suggesterItem = initSuggesterFor(props.column.type, <Suggester>{
				type: "suggest",
				array: false,
				url: "suggestfeed.do",
				minTags: 0,
				maxTags: 1,
				propagate: [
					{ d: "data", s: "id.main", c: "string", empty: "0" },
					{ d: "title", s: "title.main", empty: "" },
				],
				tag: [
					{ d: "data", t: "id", empty: "0" },
					{ d: "title", t: "name", empty: "", loadIfMissing: {
							url: "reverseSuggest.do",
							params: [{ name: "id", d: "data", empty: "0" }]
						} }
				]
			});
			fp = suggesterItem;
			break;
		case ColumnType.TEXT:
		case ColumnType.HTML:
		case ColumnType.STRING:
		case ColumnType.LINK:
		default:
			if (props.column.list) {
				fp = <Select>{
					type: "select",
					list: props.column.list,
					rewriteUnlisted: ""
				};
			} else {
				fp = <Input>{
					type: "input",
				};
			}
			break;
	}
	return fp;
}

const env = injectEnv();

function initSuggesterFor(type:ColType, sg:Partial<Suggester>):Suggester {
	switch (type) {
		case ColumnType.PARTY:
			sg.suggester = "CustomerSuggester";
			sg.srcType = SrcType.PARTY;
			break;
		case ColumnType.OPPORTUNITY:
			sg.suggester = "OpportunitySuggester";
			sg.srcType = SrcType.OPPORTUNITY;
			break;
		case ColumnType.OFFER:
			sg.suggester = "OfferSuggester";
			sg.srcType = SrcType.OFFER;
			break;
		case ColumnType.BUSCASE:
			sg.suggester = "BusCaseSuggester";
			sg.srcType = SrcType.BUSCASE;
			break;
		case ColumnType.TASK:
			sg.suggester = "TaskSuggester";
			sg.srcType = SrcType.TASK;
			break;
		case ColumnType.EVENT:
			sg.suggester = "EventSuggester";
			sg.srcType = SrcType.EVENT;
			break;
		case ColumnType.PROBLEM:
			sg.suggester = "ProblemSuggester";
			sg.srcType = SrcType.PROBLEM;
			break;
		case ColumnType.REGION:
			sg.suggester = "RegionSuggester";
			break;
		case ColumnType.CAMPAIGN:
			sg.suggester = "CampaignSuggester";
			sg.srcType = SrcType.CAMPAIGN;
			break;
		case ColumnType.PRODUCT:
			sg.suggester = "ProductSuggester";
			sg.srcType = SrcType.PRODUCT;
			break;
	}
	return sg as Suggester;
}

const arrayItemPart = computed<FormPart>(() => {
	const item = itemPart(true);
	return item;
});

const singleItemPart = computed<FormPart>(() => {
	const item = itemPart(false);
	item.property = "data";	// only first item of the array is edited
	return item;
});

function switchToValueEditMode() {
	emit("bigvalue");
	return null;
}

const multiSuggesterPart = computed<Suggester>(() => initSuggesterFor(props.column.type, {
	type: "suggest",
	property: "values",
	minTags: 1,
	maxTags: 100,
	array: true,
	url: "suggestfeed.do",
	propagate: [
		{
			d: "*",
			s: "id.main",
			c: "string",
			empty: "0"
		},
		{
			d: "$.title",	// Store ID to metadata
			s: "title.main",
			empty: ""
		}
	],
	tag: [
		{
			d: "*",
			t: "id",
			empty: "0"
		},
		{
			d: "$.title",
			t: "name",
			loadIfMissing: {
				url: "reverseSuggest.do",
				params: [
					{ name: "id", d: "*", empty: "0" }	// append id param to query: e.g. reverseSuggest.do?id=102
				]
			}
		}
	]
}));


const arrayPart = computed<Array>(() => <Array>{
		type: "array",
		property: "values",
		minItems: 1,
		add: [
			<ArrayAddButton>{
				icon: "plus-circle-14-full",
				init: "value",
				value: ""
			},
			<ArrayAddButton>{
				icon: "value-edit",
				init: "function",
				value: switchToValueEditMode
			}
		],
		removeIcon: "minus-circle-14-full",
		item: arrayItemPart.value
	});

const useList = computed<boolean>(() => {
	return props.column != null && !!props.column.list;
});

watch(() => props.bigValue, (now, old) => {
	// When switching between big-editor and smaller array
	// content of the textarea must be synchronized
	if (now && !old) {
		// Switching from array to textarea
		arrayChanged();
	} else if (!now && old) {
		// Switching from textarea to array
		textAreaChanged();
	}
});

// watch(() => props.fi.values, (now, old) => {
//
// }, { deep: true });

</script>

<style>
.value-editor {
	resize: vertical !important;
}
</style>
